import { useState, memo } from "react";
import { motion } from "framer-motion";
import { Avatar } from "@heroui/react";
import { cn } from "../../lib/cn";
import { getFullName } from "../../utils/utils";
import { CustomPopover } from "../shared";
import { DotsIcon, EditIcon, DeleteIcon, MailIcon, PhoneIcon } from "../../assets/icons";
import { PROJECT_GRADIENTS } from "../../constants/gradients";
import { PROJECT_ROLE_LABELS } from "../../constants/roles";

const STATUS_DOTS = {
  active: "bg-success",
  pending: "bg-warning/100",
  inactive: "bg-mediumgrey",
};

const ProjectMemberCard = memo(function ProjectMemberCard({
  member,
  onEdit,
  onRemove,
  onCancelInvitation,
  canCancelInvitation = false,
  isCancelling = false,
  gradient = "gradient-4",
  variants,
}) {
  const [menuOpen, setMenuOpen] = useState(false);

  const user = member?.user || {};
  const fullName = user?.full_name || getFullName(user) || "Unknown";
  const initials = fullName
    .split(" ")
    .map((n) => n?.[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const title = member?.title || "";
  const email = user?.email || "";
  const phone = user?.phone_no || "";
  const skills = user?.skills || [];
  const status = (member?.status || "inactive").toLowerCase();
  const activeTaskCount = member?.active_task_count ?? 0;
  const role = member?.role || "COLLABORATOR";
  const isExpiredInvitation = status === "pending" && member?.is_invitation_expired;

  const gradientClass = PROJECT_GRADIENTS[gradient] || PROJECT_GRADIENTS["gradient-4"];
  const visibleSkills = skills.slice(0, 3);
  const remainingSkills = Math.max(0, skills.length - 3);

  return (
    <motion.div
      variants={variants}
      whileHover={variants ? undefined : { y: -4, scale: 1.01 }}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
      className="h-full"
    >
      <div className="group relative h-full border border-surface-border/60 rounded-2xl bg-white shadow-sm hover:shadow-xl hover:shadow-surface-active/50 transition-all duration-300 select-none overflow-hidden ring-1 ring-primary/5 flex flex-col">
        {/* Header with gradient */}
        <div className={cn("relative bg-gradient-to-br overflow-hidden", gradientClass)}>
          {/* Glass effect overlay */}
          <div className="absolute inset-0 bg-white/10 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

          {/* Decorative shapes */}
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-black/5 rounded-full blur-2xl" />

          {/* Three-dot menu */}
          <div className="absolute top-3 right-3 z-20">
            <CustomPopover
              trigger={
                <button
                  type="button"
                  aria-label={`Options for ${fullName}`}
                  className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
                >
                  <DotsIcon className="text-white" width={16} height={16} />
                </button>
              }
              open={menuOpen}
              setOpen={setMenuOpen}
            >
              <div className="flex flex-col gap-1 py-1 min-w-[140px]">
                {canCancelInvitation && isExpiredInvitation && (
                  <button
                    type="button"
                    aria-label="Cancel invitation"
                    className="flex items-center gap-2 px-3 py-2 text-sm text-danger hover:bg-danger/10 rounded-md transition-colors disabled:opacity-50"
                    onClick={() => {
                      setMenuOpen(false);
                      onCancelInvitation?.(member);
                    }}
                    disabled={isCancelling}
                  >
                    <DeleteIcon width={14} height={14} />
                    Cancel invitation
                  </button>
                )}
                <button
                  type="button"
                  className="flex items-center gap-2 px-3 py-2 text-sm text-darkgrey hover:bg-lightgrey rounded-md transition-colors"
                  onClick={() => {
                    setMenuOpen(false);
                    onEdit?.(member);
                  }}
                >
                  <EditIcon width={14} height={14} />
                  Edit member
                </button>
                <button
                  type="button"
                  className="flex items-center gap-2 px-3 py-2 text-sm text-danger hover:bg-danger/10 rounded-md transition-colors"
                  onClick={() => {
                    setMenuOpen(false);
                    onRemove?.(member);
                  }}
                >
                  <DeleteIcon width={14} height={14} />
                  Remove from project
                </button>
              </div>
            </CustomPopover>
          </div>

          {/* Avatar and name */}
          <div className="relative z-10 px-5 py-5 flex items-center gap-3">
            {user?.profile_image_url ? (
              <Avatar
                src={user.profile_image_url}
                className="ring-2 ring-white/30"
                size="lg"
              />
            ) : (
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-white/20 ring-2 ring-white/30 text-white font-semibold text-sm">
                {initials}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-sans font-bold text-sm truncate drop-shadow-sm">
                {fullName}
              </h3>
              {title && (
                <p className="text-white/80 text-xs truncate mt-0.5">{title}</p>
              )}
            </div>
          </div>
        </div>

        {/* Body content */}
        <div className="p-4 space-y-3 flex-1">
          {/* Email */}
          {email && (
            <div className="flex items-center gap-2 text-xs text-darkgrey">
              <MailIcon width={14} height={14} className="text-grey flex-shrink-0" />
              <span className="truncate">{email}</span>
            </div>
          )}

          {/* Phone */}
          {phone && (
            <div className="flex items-center gap-2 text-xs text-darkgrey">
              <PhoneIcon width={14} height={14} className="text-grey flex-shrink-0" />
              <span>{phone}</span>
            </div>
          )}

          {/* Skills */}
          {skills.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {visibleSkills.map((skill, idx) => (
                <span
                  key={skill?.id || idx}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-lightgrey text-darkgrey border border-surface-border"
                >
                  {skill?.name || skill}
                </span>
              ))}
              {remainingSkills > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-lightgrey text-grey">
                  +{remainingSkills} more
                </span>
              )}
            </div>
          )}

          {isExpiredInvitation && (
            <p className="text-xs font-medium text-danger">Invitation expired</p>
          )}
        </div>

        {/* Footer with status and task count */}
        <div className="px-4 py-3 border-t border-surface-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider">
              <span
                className={cn(
                  "w-1.5 h-1.5 rounded-full",
                  STATUS_DOTS[status] || STATUS_DOTS.inactive
                )}
                aria-hidden="true"
              />
              <span className={cn(
                status === "active" ? "text-grey" : isExpiredInvitation ? "text-danger" : status === "pending" ? "text-warning" : "text-mediumgrey"
              )}>
                {isExpiredInvitation ? "expired" : status}
              </span>
            </span>
            <span className="text-[10px] text-grey font-medium uppercase tracking-wide">
              {PROJECT_ROLE_LABELS[role] || role?.toLowerCase()}
            </span>
          </div>
          <span className="text-xs text-darkgrey">
            {activeTaskCount} active task{activeTaskCount !== 1 ? "s" : ""}
          </span>
        </div>
      </div>
    </motion.div>
  );
});

export default ProjectMemberCard;
